/**
 * Sentry Error Monitoring Integration
 *
 * Captures runtime errors, React component crashes, and unhandled promise rejections.
 * Enabled only in staging/production environments.
 *
 * Privacy: All PII (emails, phone numbers, license plates, addresses) is filtered
 * before events are sent to Sentry.
 */

import * as Sentry from '@sentry/react';

// Environment configuration
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const APP_ENV = import.meta.env.VITE_APP_ENV || 'development';
const APP_VERSION = import.meta.env.VITE_APP_VERSION || 'unknown';
const FIREBASE_PROJECT_ID = import.meta.env.VITE_FIREBASE_PROJECT_ID;

// PII patterns for filtering
const PII_PATTERNS = {
  // Email addresses
  email: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
  // Phone numbers (international and local formats)
  phone: /(\+?\d{1,4}[-.\s]?)?(\(?\d{1,4}\)?[-.\s]?)?[\d\s.-]{6,}/g,
  // Dutch license plates (various formats: XX-XX-XX, X-XXX-XX, XX-XXX-X, etc.)
  licensePlate: /\b[A-Z0-9]{1,3}[-\s]?[A-Z0-9]{1,3}[-\s]?[A-Z0-9]{1,3}\b/gi,
  // Postal codes (Dutch format: 1234 AB)
  postalCode: /\b\d{4}\s?[A-Z]{2}\b/gi,
  // Street addresses (basic pattern)
  streetAddress: /\b\d+\s+[A-Za-z]+\s+(straat|weg|laan|plein|avenue|street|road|lane)\b/gi,
};

/**
 * Mask PII in a string
 */
function maskPII(value: string): string {
  if (!value || typeof value !== 'string') return value;

  let masked = value;
  masked = masked.replace(PII_PATTERNS.email, '[EMAIL]');
  masked = masked.replace(PII_PATTERNS.phone, '[PHONE]');
  masked = masked.replace(PII_PATTERNS.licensePlate, '[LICENSE]');
  masked = masked.replace(PII_PATTERNS.postalCode, '[POSTAL]');
  masked = masked.replace(PII_PATTERNS.streetAddress, '[ADDRESS]');

  return masked;
}

/**
 * Recursively mask PII in an object
 */
function maskPIIInObject(obj: unknown): unknown {
  if (obj === null || obj === undefined) return obj;

  if (typeof obj === 'string') {
    return maskPII(obj);
  }

  if (Array.isArray(obj)) {
    return obj.map(maskPIIInObject);
  }

  if (typeof obj === 'object') {
    const masked: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      // Skip sensitive field names entirely
      const lowerKey = key.toLowerCase();
      if (
        lowerKey.includes('email') ||
        lowerKey.includes('phone') ||
        lowerKey.includes('address') ||
        lowerKey.includes('license') ||
        lowerKey.includes('plate') ||
        lowerKey.includes('name') ||
        lowerKey.includes('password') ||
        lowerKey.includes('token') ||
        lowerKey.includes('secret') ||
        lowerKey.includes('auth')
      ) {
        masked[key] = '[REDACTED]';
      } else {
        masked[key] = maskPIIInObject(value);
      }
    }
    return masked;
  }

  return obj;
}

/**
 * beforeSend hook to filter PII from events
 */
function beforeSend(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  // Filter PII from exception messages
  if (event.exception?.values) {
    event.exception.values = event.exception.values.map((ex) => ({
      ...ex,
      value: ex.value ? maskPII(ex.value) : ex.value,
    }));
  }

  // Filter PII from breadcrumbs
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => ({
      ...breadcrumb,
      message: breadcrumb.message ? maskPII(breadcrumb.message) : breadcrumb.message,
      data: breadcrumb.data
        ? (maskPIIInObject(breadcrumb.data) as Record<string, unknown>)
        : breadcrumb.data,
    }));
  }

  // Filter PII from extra context
  if (event.extra) {
    event.extra = maskPIIInObject(event.extra) as Record<string, unknown>;
  }

  // Remove request headers that might contain tokens
  if (event.request) {
    delete event.request.headers;
    delete event.request.cookies;
  }

  // Never include user email or name
  if (event.user) {
    delete event.user.email;
    delete event.user.username;
    // Keep only safe identifiers
    event.user = {
      id: event.user.id,
    };
  }

  return event;
}

/**
 * Check if Sentry should be enabled
 */
export function isSentryEnabled(): boolean {
  // Must have DSN configured
  if (!SENTRY_DSN) return false;

  // Disabled in development unless explicitly enabled
  if (APP_ENV === 'development') {
    return import.meta.env.VITE_SENTRY_ENABLED_DEV === 'true';
  }

  return true;
}

/**
 * Initialize Sentry
 * Call this early in your app's lifecycle (e.g., in main.tsx before React renders)
 */
export function initSentry(): void {
  if (!isSentryEnabled()) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console -- Dev-only Sentry status message
      console.info('[Sentry] Disabled - no DSN configured or development environment');
    }
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: APP_ENV,
    release: APP_VERSION,

    // Performance monitoring (basic)
    tracesSampleRate: APP_ENV === 'production' ? 0.1 : 0.5,

    // Session replay disabled for privacy
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Mask all text for privacy
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // PII filtering
    beforeSend,

    // Don't send PII in default tags
    initialScope: {
      tags: {
        'app.env': APP_ENV,
        'firebase.project': FIREBASE_PROJECT_ID || 'unknown',
      },
    },

    // Ignore common non-actionable errors
    ignoreErrors: [
      // Browser extensions
      'ResizeObserver loop',
      'ResizeObserver loop limit exceeded',
      // Network errors (handled by app)
      'Failed to fetch',
      'NetworkError',
      'Load failed',
      // Firebase auth (handled by app)
      'auth/network-request-failed',
      'auth/popup-closed-by-user',
      'auth/cancelled-popup-request',
    ],

    // Don't capture errors from browser extensions
    denyUrls: [/extensions\//i, /^chrome:\/\//i, /^chrome-extension:\/\//i, /^moz-extension:\/\//i],
  });
}

/**
 * Set user context for Sentry
 * Only includes non-PII identifiers
 */
export function setSentryUser(user: { uid: string; teamId?: string; role?: string }): void {
  if (!isSentryEnabled()) return;

  Sentry.setUser({
    id: user.uid,
  });

  if (user.teamId) {
    Sentry.setTag('team.id', user.teamId);
  }

  if (user.role) {
    Sentry.setTag('user.role', user.role);
  }
}

/**
 * Clear user context (call on logout)
 */
export function clearSentryUser(): void {
  if (!isSentryEnabled()) return;

  Sentry.setUser(null);
  Sentry.setTag('team.id', undefined);
  Sentry.setTag('user.role', undefined);
}

/**
 * Set request context for correlation with Cloud Functions logs
 *
 * Call this before making a Cloud Function call to ensure errors
 * can be correlated with the same requestId in Cloud Logging.
 *
 * @param requestId - The request correlation ID
 * @param teamId - Optional team ID for context
 */
export function setRequestContext(requestId: string, teamId?: string): void {
  if (!isSentryEnabled()) return;

  Sentry.setTag('request.id', requestId);
  if (teamId) {
    Sentry.setTag('team.id', teamId);
  }
}

/**
 * Clear request context (call after operation completes)
 */
export function clearRequestContext(): void {
  if (!isSentryEnabled()) return;

  Sentry.setTag('request.id', undefined);
}

/**
 * Capture an exception with optional context
 */
export function captureException(
  error: Error,
  context?: Record<string, unknown>
): string | undefined {
  if (!isSentryEnabled()) return undefined;

  return Sentry.captureException(error, {
    extra: context ? (maskPIIInObject(context) as Record<string, unknown>) : undefined,
  });
}

/**
 * Capture a message with optional context
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = 'info',
  context?: Record<string, unknown>
): string | undefined {
  if (!isSentryEnabled()) return undefined;

  return Sentry.captureMessage(maskPII(message), {
    level,
    extra: context ? (maskPIIInObject(context) as Record<string, unknown>) : undefined,
  });
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(breadcrumb: Sentry.Breadcrumb): void {
  if (!isSentryEnabled()) return;

  Sentry.addBreadcrumb({
    ...breadcrumb,
    message: breadcrumb.message ? maskPII(breadcrumb.message) : breadcrumb.message,
    data: breadcrumb.data
      ? (maskPIIInObject(breadcrumb.data) as Record<string, unknown>)
      : breadcrumb.data,
  });
}

/**
 * Set additional context
 */
export function setContext(name: string, context: Record<string, unknown>): void {
  if (!isSentryEnabled()) return;

  Sentry.setContext(name, maskPIIInObject(context) as Record<string, unknown>);
}

// Re-export Sentry's ErrorBoundary for convenience
export { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';

// Re-export withProfiler for component performance tracking
export { withProfiler } from '@sentry/react';

/**
 * Test function to verify Sentry is working (DEV only)
 * Call from browser console: window.__testSentry()
 */
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__testSentry = () => {
    // eslint-disable-next-line no-console -- Dev-only test function
    console.log('[Sentry Test] Enabled:', isSentryEnabled());
    // eslint-disable-next-line no-console -- Dev-only test function
    console.log('[Sentry Test] Environment:', APP_ENV);

    if (!isSentryEnabled()) {
      // eslint-disable-next-line no-console -- Dev-only test function
      console.error('[Sentry Test] Sentry is NOT enabled. Check VITE_SENTRY_DSN and VITE_APP_ENV.');
      return;
    }

    const testError = new Error('Sentry test error - ' + new Date().toISOString());
    const eventId = Sentry.captureException(testError);
    // eslint-disable-next-line no-console -- Dev-only test function
    console.log('[Sentry Test] Event captured with ID:', eventId);

    return eventId;
  };
}
